import React, { useImperativeHandle, useState } from "react";
import debounce from "lodash/debounce";
import { usePathname, useRouter } from "next/navigation";

import Tab from "@/components/botanic/Tab";
import Tabs from "@/components/botanic/Tabs";
import SearchIcon from "@/components/Icons/searchBar/search.svg";
import CloseIcon from "@/components/Icons/searchBar/x.svg";

enum LocationFilter {
  ALL = "all",
  NEARBY = "nearby",
}

export type BrandListHeaderProps = {
  H1Text: string;
  searchQuery: string | null;
};

export interface HeaderComponentRef {
  setSearch: () => void;
}

const BrandListHeader = React.forwardRef<
  HeaderComponentRef,
  BrandListHeaderProps
>(({ H1Text, searchQuery = "" }: BrandListHeaderProps, ref) => {
  const [searchTerm, setSearchTerm] = useState(searchQuery || "");

  const router = useRouter();
  const path = usePathname();

  const isAllBrandsPage = path?.includes("/all");

  // Expose the reset function to the parent using useImperativeHandle
  useImperativeHandle(ref, () => ({
    setSearch: () => setSearchTerm(""),
  }));

  const getURLForTab = (location: LocationFilter) => {
    return `/brands${location === LocationFilter.ALL ? "/all" : ""}${
      searchQuery ? `?search=${searchQuery}` : ""
    }`;
  };

  const search = debounce((event) => {
    // this will stop ?search= from being added to the query params
    if (!event.target.value && !searchTerm) {
      return;
    }
    router.push(`${path}?search=${event.target.value || searchTerm}`);
  }, 300);

  return (
    <div className="container">
      <h1 className="heading--m mb-md">
        {searchTerm ? `Showing results for ${searchTerm}` : H1Text}
      </h1>
      <div className="relative mb-lg">
        <div className="absolute top-0 left-1.5 flex h-full text-default">
          <button className="px-lg" type="submit" aria-label="Search brands">
            <SearchIcon
              className="flex-shrink-0 block search__icon"
              height="16"
              width="16"
            />
          </button>
        </div>
        <input
          className="pl-10 p-sm bg-white"
          aria-label="Brand name search"
          data-testid="search-input"
          placeholder="Search brands"
          type="text"
          value={searchTerm}
          onChange={(e) => {
            search(e);
            setSearchTerm(e.target.value);
          }}
        />
        {searchTerm && (
          <div className="absolute top-0 right-0 flex h-full text-default">
            <button
              className="px-sm"
              type="button"
              aria-label="Clear search"
              data-testid="clear-search-icon"
              onClick={() => {
                setSearchTerm("");
                router.push(path || "/brands/all");
              }}
            >
              <CloseIcon
                className="flex-shrink-0 block search__icon"
                height="15"
                width="15"
              />
            </button>
          </div>
        )}
      </div>
      <Tabs className="mb-xl" ariaLabel="Brands page">
        <Tab
          active={!isAllBrandsPage}
          href={getURLForTab(LocationFilter.NEARBY)}
        >
          Available nearby
        </Tab>
        <Tab active={isAllBrandsPage} href={getURLForTab(LocationFilter.ALL)}>
          All brands
        </Tab>
      </Tabs>
    </div>
  );
});

export default BrandListHeader;
